<template>
  <div class="my-2 mx-8">
    <settings-section
      :title="$t('INBOX_MGMT.BUSINESS_HOURS.TITLE')"
      :sub-title="$t('INBOX_MGMT.BUSINESS_HOURS.SUBTITLE')"
    >
      <form @submit.prevent="updateInbox">
        <label for="toggle-business-hours" class="toggle-input-wrap">
          <label
            v-if="isOnlineWaInboxV2"
            for="toggle-business-hours"
            class="toggle-input-wrap"
          >
            <input
              v-model="botDisable"
              type="checkbox"
              name="toggle-business-hours"
            />
            {{ $t('INBOX_MGMT.BUSINESS_HOURS.TOGGLE_DISABLE_BOT') }}
          </label>
          <div v-if="botDisable && isAWhatsAppChannel" class="bot-disable-wrap">
            <label>
              {{ $t('INBOX_MGMT.BUSINESS_HOURS.DISABLE_BOT_LABEL') }}
            </label>
            <select v-model="botDisableValue">
              <option
                v-for="option in statusBotOptions"
                :key="option.value"
                :value="option.value"
                :disabled="option.value === 'outside' && !isBusinessHoursEnabled"
              >{{ option.title }}</option>
            </select>
          </div>
          <input
            v-model="isBusinessHoursEnabled"
            type="checkbox"
            name="toggle-business-hours"
          />
          {{ $t('INBOX_MGMT.BUSINESS_HOURS.TOGGLE_AVAILABILITY') }}
        </label>
        <p>{{ $t('INBOX_MGMT.BUSINESS_HOURS.TOGGLE_HELP') }}</p>
        <div v-if="isBusinessHoursEnabled" class="mb-6">
          <div class="max-w-[37.5rem]">
            <label class="unavailable-input-wrap">
              {{ $t('INBOX_MGMT.BUSINESS_HOURS.UNAVAILABLE_MESSAGE_LABEL') }}
            </label>
            <div
              v-if="isRichEditorEnabled"
              class="py-0 px-4 border border-solid border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 rounded-md mx-0 mt-0 mb-4"
            >
              <woot-message-editor
                v-model="unavailableMessage"
                :enable-variables="true"
                :is-format-mode="true"
                class="message-editor"
                :min-height="4"
              />
            </div>
            <textarea v-else v-model="unavailableMessage" type="text" />
          </div>
          <div class="timezone-input-wrap">
            <label>
              {{ $t('INBOX_MGMT.BUSINESS_HOURS.TIMEZONE_LABEL') }}
            </label>
            <multiselect
              v-model="timeZone"
              :options="timeZones"
              deselect-label=""
              select-label=""
              selected-label=""
              track-by="value"
              label="label"
              :close-on-select="true"
              :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
              :allow-empty="false"
            />
          </div>

          <label>
            {{ $t('INBOX_MGMT.BUSINESS_HOURS.WEEKLY_TITLE') }}
          </label>
          <business-day
            v-for="timeSlot in timeSlots"
            :key="timeSlot.day"
            :day-name="dayNames[timeSlot.day]"
            :time-slot="timeSlot"
            @update="data => onSlotUpdate(timeSlot.day, data)"
          />
        </div>
        <woot-submit-button
          :button-text="$t('INBOX_MGMT.BUSINESS_HOURS.UPDATE')"
          :loading="uiFlags.isUpdating"
          :disabled="hasError"
        />
      </form>
    </settings-section>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import alertMixin from 'shared/mixins/alertMixin';
import inboxMixin from 'shared/mixins/inboxMixin';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import BusinessDay from './BusinessDay.vue';
import {
  timeSlotParse,
  timeSlotTransform,
  defaultTimeSlot,
  timeZoneOptions,
} from '../helpers/businessHour';

const DEFAULT_TIMEZONE = { // d99d
  label: 'Jerusalem (GMT+03:00)',
  value: 'Asia/Jerusalem',
};

const DEFAULT_BOT_VALUE = 1; // d99d

export default {
  components: {
    SettingsSection,
    BusinessDay,
    WootMessageEditor,
  },
  mixins: [alertMixin, inboxMixin],
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isBusinessHoursEnabled: false,
      botDisable: false,
      botDisableValue: DEFAULT_BOT_VALUE,
      statusBotOptions: [
        {
          title: this.$t('INBOX_MGMT.BUSINESS_HOURS.STATUS_BOT_OPTIONS.1_HOUR'),
          value: 1,
        },
        {
          title: this.$t('INBOX_MGMT.BUSINESS_HOURS.STATUS_BOT_OPTIONS.10_HOURS'),
          value: 10,
        },
        {
          title: this.$t('INBOX_MGMT.BUSINESS_HOURS.STATUS_BOT_OPTIONS.24_HOURS'),
          value: 24,
        },
        {
          title: this.$t('INBOX_MGMT.BUSINESS_HOURS.STATUS_BOT_OPTIONS.OUTSIDE'),
          value: 'outside',
        },
        {
          title: this.$t('INBOX_MGMT.BUSINESS_HOURS.STATUS_BOT_OPTIONS.ALWAYS'),
          value: 'always',
        },
      ],
      unavailableMessage: '',
      timeZone: DEFAULT_TIMEZONE,
      dayNames: {
        0: this.$t('WEEK_DAYS.SUNDAY'),
        1: this.$t('WEEK_DAYS.MONDAY'),
        2: this.$t('WEEK_DAYS.TUESDAY'),
        3: this.$t('WEEK_DAYS.WEDNESDAY'),
        4: this.$t('WEEK_DAYS.THURSDAY'),
        5: this.$t('WEEK_DAYS.FRIDAY'),
        6: this.$t('WEEK_DAYS.SATURDAY'),
      },
      timeSlots: [...defaultTimeSlot],
    };
  },
  computed: {
    ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }),
    hasError() {
      if (!this.isBusinessHoursEnabled) return false;
      return this.timeSlots.filter(slot => slot.from && !slot.valid).length > 0;
    },
    timeZones() {
      return [...timeZoneOptions()];
    },
    isRichEditorEnabled() {
      if (
        this.isATwilioChannel ||
        this.isATwitterInbox ||
        this.isAFacebookInbox
      )
        return false;
      return true;
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
    isBusinessHoursEnabled() {
      if (!this.isBusinessHoursEnabled && this.botDisableValue === 'outside')
        this.botDisableValue = DEFAULT_BOT_VALUE;
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      const {
        working_hours_enabled: isEnabled = false,
        out_of_office_message: unavailableMessage,
        working_hours: timeSlots = [],
        timezone: timeZone,
        disable_chatbot: disable_chatbot,
      } = this.inbox;
      const slots = timeSlotParse(timeSlots).length
        ? timeSlotParse(timeSlots)
        : defaultTimeSlot;
      this.isBusinessHoursEnabled = isEnabled;
      this.unavailableMessage = unavailableMessage || '';
      this.timeSlots = slots;
      this.timeZone =
        this.timeZones.find(item => timeZone === item.value) ||
        DEFAULT_TIMEZONE;
      if (disable_chatbot) {
        this.botDisableValue = disable_chatbot;
        this.botDisable = true;
      }
    },
    onSlotUpdate(slotIndex, slotData) {
      this.timeSlots = this.timeSlots.map(item =>
        item.day === slotIndex ? slotData : item
      );
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          working_hours_enabled: this.isBusinessHoursEnabled,
          out_of_office_message: this.unavailableMessage,
          working_hours: timeSlotTransform(this.timeSlots),
          timezone: this.timeZone.value,
          channel: {},
          disable_chatbot: this.botDisable ? this.botDisableValue : 'clear',
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        this.showAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        this.showAlert(
          error.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.timezone-input-wrap,
.bot-disable-wrap {
  @apply max-w-[37.5rem];

  &::v-deep .multiselect {
    @apply mt-2;
  }
}

::v-deep.message-editor {
  @apply border-0;
}

.unavailable-input-wrap {
  @apply max-w-[37.5rem];

  textarea {
    @apply min-h-[4rem] mt-2;
  }
}
</style>
